import React from 'react';
export function Problem() {
  return <section className="py-16 px-4 bg-[#EFE4CC]" data-id="element-142">
      <div className="max-w-3xl mx-auto space-y-12" data-id="element-143">
        {/* Intro Problem */}
        <div className="space-y-6 text-lg md:text-xl text-slate-700 leading-relaxed" data-id="element-144">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center" data-id="element-145">
            El problema no es que no te importa. Es que no sabés qué hacer.
          </h3>

          <p data-id="element-146">Si estás leyendo esto, seguramente conocés la escena de cerca.</p>

          <p data-id="element-147">
            Estás en el almuerzo del domingo. Tu papá o tu mamá empieza a contar
            algo y en el medio de la historia se detiene. Busca la palabra. No
            la encuentra. Se ríe para disimular y dice "uy, se me fue". Todos
            siguen con la conversación como si nada.
          </p>

          <p data-id="element-148">Pero vos te quedaste pensando.</p>

          <p data-id="element-149">
            Porque no es la primera vez. Hace unos meses empezaron a olvidar
            cosas simples. El nombre de alguien conocido. El turno del médico
            que tenían anotado. Dónde dejaron las llaves. Si ya tomaron la
            medicación.
          </p>

          <p data-id="element-150">Y lo más angustiante no es el olvido en sí.</p>

          <p data-id="element-151">
            Es que no sabés si es normal. No sabés si va a empeorar. No sabés
            qué podés hacer vos como hijo, como nieto, como familiar que los
            quiere y que vive con esa preocupación guardada en algún rincón de
            la cabeza.
          </p>

          <p data-id="element-152">
            Y encima la vida no para. Trabajás. Tenés tus propias obligaciones.
            No podés estar todos los días con ellos. Y cuando los ves, querés
            que ese tiempo valga. Querés que estén bien. Querés hacer algo
            concreto, no solo preocuparte.
          </p>
        </div>

        {/* What happens in most families */}
        <div className="bg-white rounded-2xl p-8 md:p-10 border border-orange-200 shadow-md space-y-6 text-lg text-slate-700" data-id="element-153">
          <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-154">
            Lo que pasa en la mayoría de las familias argentinas cuando aparecen
            los olvidos
          </h3>

          <p data-id="element-155">Uno espera a ver si mejora solo y no mejora.</p>
          <p data-id="element-156">
            Otro lo lleva al médico, el médico dice "es la edad" y no da ninguna
            herramienta concreta para trabajarlo en casa.
          </p>
          <p data-id="element-157">
            El de al lado busca en Google y encuentra información en inglés,
            estudios científicos que no entiende o productos carísimos que no
            sabe si funcionan.
          </p>
          <p data-id="element-158">
            Alguien le compra una aplicación para el celular pero el adulto
            mayor no la usa porque no sabe manejarla o directamente no quiere.
          </p>
          <p className="font-semibold text-slate-900" data-id="element-159">
            Y casi todos terminan en el mismo lugar: haciendo nada. No porque no
            les importe. Sino porque no saben exactamente qué hacer ni cómo
            hacerlo.
          </p>
          <p data-id="element-160">
            Mientras tanto el adulto mayor sigue solo en casa. Con la tele de
            fondo. Con los mismos pensamientos de siempre. Sin nadie que lo
            desafíe mentalmente. Sin una rutina que mantenga su mente activa.
          </p>
          <p className="text-teal-700 font-medium italic" data-id="element-161">
            Y el cerebro, como cualquier músculo que no se usa, se va apagando
            de a poco.
          </p>
        </div>

        {/* The real problem */}
        <div className="space-y-8" data-id="element-162">
          <h3 className="text-3xl font-bold text-slate-900 text-center mb-10" data-id="element-163">
            El verdadero problema de la estimulación cognitiva en Argentina
          </h3>

          <p className="text-xl text-center text-slate-700 mb-10" data-id="element-164">
            El cerebro no envejece solo por los años. Envejece por la falta de
            uso.
          </p>

          <p className="text-lg text-slate-600 mb-8" data-id="element-165">
            Pero hay tres razones concretas por las que la mayoría de las
            familias argentinas no logra trabajar esto en casa:
          </p>

          <div className="space-y-8" data-id="element-166">
            <div className="bg-white p-6 rounded-xl border-l-4 border-teal-600 shadow-sm" data-id="element-167">
              <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-168">
                Razón 1: No existe un sistema ordenado y adaptado a nuestra
                realidad.
              </h4>
              <p className="text-slate-700" data-id="element-169">
                Todo lo que existe está pensado para profesionales de la salud,
                clínicas especializadas o está en otro idioma. No hay un sistema
                simple, claro y listo para usar que una familia argentina pueda
                aplicar en casa sin tener ningún conocimiento previo.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-l-4 border-teal-600 shadow-sm" data-id="element-170">
              <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-171">
                Razón 2: El adulto mayor no quiere hacer cosas que lo hagan
                sentir "enfermo" o "viejo".
              </h4>
              <p className="text-slate-700" data-id="element-172">
                Los ejercicios cognitivos genéricos son aburridos, clínicos y
                hacen sentir que hay algo malo. Un abuelo que se siente evaluado
                no participa. Un abuelo que se siente protagonista de algo
                valioso, sí.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border-l-4 border-teal-600 shadow-sm" data-id="element-173">
              <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-174">
                Razón 3: La familia no sabe cómo involucrarse sin que parezca
                una terapia.
              </h4>
              <p className="text-slate-700" data-id="element-175">
                Querés ayudarlo pero no querés que se sienta señalado. Querés
                pasar tiempo de calidad pero no sabés cómo convertir ese tiempo
                en estimulación real. Y terminás sin hacer nada porque no querés
                incomodar.
              </p>
            </div>
          </div>

          <div className="text-center pt-8 space-y-4" data-id="element-176">
            <p className="text-2xl font-bold text-slate-900" data-id="element-177">
              No es falta de amor. No es falta de voluntad.
            </p>
            <p className="text-xl text-teal-700 font-medium" data-id="element-178">
              Es falta de un sistema que funcione de verdad para familias
              argentinas reales.
            </p>
          </div>
        </div>
      </div>
    </section>;
}